import React, { useRef, useEffect, useState, useMemo, useCallback } from "react";
import * as d3 from "d3";
import { useTheme } from "../contexts/ThemeContext";
import { IMatchTimelineProps, IMatches } from "../types/ITimeline";
import MatchDetailCard from "./MatchDetailCard";
import { useLoading } from "../contexts/LoadingContext";
import { postData } from "../services/api";
import IApiResponse from "../types/IApiResponse";
import handleApiResponse from "../utils/handleApiResponse";
import apiUrls from "../config/apiUrls";
import { IMatchEventCardData } from "../types/IMatchEventCardData";
import MatchIcon from '../assets/icons/matchTimelineIcon.svg';
import noImageClub from "../assets/icons/noImageClub.svg";

const MatchTimeLine: React.FC<IMatchTimelineProps> = React.memo(
  ({ matches = [], onSelectMatchId, timelineDirection, zoomLevel, selectedMatchId, teamId }) => {
    const { theme } = useTheme();
    const { startLoading, stopLoading } = useLoading();
    const svgRef = useRef<SVGSVGElement | null>(null);
    const matchData = useMemo(() => matches.slice(), [matches]);
    const [direction, setDirection] = useState<string>("next");
    const [matchCardPosition, setMatchCardPosition] = useState<{ x: number; y: number } | null>(null);
    const [isMatchCardOpen, setIsMatchCardOpen] = useState(false);
    const [selectedMatch, setSelectedMatch] = useState<number>(0);
    const [matchCardDetails, setMatchCardDeatils] = useState<IMatchEventCardData | null>();

    useEffect(() => {
      setDirection(timelineDirection);
    }, [timelineDirection]);

    const fetchMatchData = async (matchId: number) => {
      try {
        startLoading();
        const response: IApiResponse<IMatchEventCardData> = await postData(`${apiUrls.GetMatchEventsCardData}`,
          {
            "matchId": matchId
          });
        handleApiResponse(response, (matchCardData) => {
          stopLoading();
          setMatchCardDeatils(matchCardData);
          setIsMatchCardOpen(true);
          return matchCardData;
        },
          (error) => {
            stopLoading();
            console.error("Error fetching match Data:", error);
          }
        );
      } catch (error) {
        stopLoading();
        console.error('Error fetching match data:', error);
        return null;
      }
    };

    const adjustPositionForPlayerCard = (x: number, y: number) => {
      const container = document.getElementById("match-timeline-container");
      if (!container) return;
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;
      const cardWidth = 508; // Approximate width of the player card
      const cardHeight = 200; // Approximate height of the player card
      let adjustedX = x;
      if (x + cardWidth > containerWidth) {
        adjustedX = containerWidth - cardWidth;
      } else if (x < 0) {
        adjustedX = 0;
      }
      const adjustedY = y + cardHeight > containerHeight ? y - cardHeight : y;
      setMatchCardPosition({ x: adjustedX, y: y });
    };

    const handleMatchClick = useCallback((selectedMatch: IMatches, event: any) => {
      fetchMatchData(selectedMatch.matchId);
      const [x, y] = d3.pointer(event);
      adjustPositionForPlayerCard(x, y);
      setSelectedMatch(selectedMatch.matchId);
    }, [onSelectMatchId]);

    const createMatchTimeline = useCallback(() => {
      setIsMatchCardOpen(false)
      const svg = d3.select(svgRef.current);
      const container = document.getElementById("match-timeline-container");
      if (!container) return;
      const timelineWidth = container.clientWidth;
      const height = 100;
      const margin =  window.innerWidth < 1650 ? 15 : window.innerWidth > 2300 ? 35 : 25; // Adjust this value to set the margin size
      svg.selectAll("*").remove();
      svg.selectAll("*").interrupt();
      const xScale = d3.scaleLinear().domain([0, matchData.length]).range([margin, timelineWidth]);
      svg
        .append("line")
        .attr("x1", 0)
        .attr("y1", height / 2)
        .attr("x2", timelineWidth)
        .attr("y2", height / 2)
        .style("stroke", theme.playerCardBorderColor)
        .style("stroke-width", 2);

      if (zoomLevel === 4 && teamId > 0 && matchData.length === 0) {
        const rectWidth = 370;
        const rectHeight = 35;
        const rectX = (timelineWidth / 2) - (rectWidth / 2);  // Centered on the line
        const rectY = (height / 2) - (rectHeight / 2);        // Centered on the line
        svg.append("rect")
          .attr("x", rectX)
          .attr("y", rectY)
          .attr("width", rectWidth)
          .attr("height", rectHeight)
          .attr("rx", 2)  // Apply horizontal border radius
          .attr("ry", 2)
          .attr("class", "match-timeline-rect")
          .style("fill", theme.playerContentBackground)
          .style("stroke", theme.playerCardBorderColor)
          .style("stroke-width", 1);
        const imageWidth = 24;
        const imageHeight = 24;
        const imageX = rectX + 10; // Adjusted to be inside the rectangle
        const imageY = rectY + (rectHeight - imageHeight) / 2; // Centered vertically
        svg.append("image")
          .attr("x", imageX) // Adjust the x position inside the rect
          .attr("y", imageY) // Adjust the y position inside the rect
          .attr("width", imageWidth)  // Adjust the image width
          .attr("height", imageHeight) // Adjust the image height
          .attr("class", theme.mode === 'dark' ? "match-timeline-image" : '')
          .attr("xlink:href", MatchIcon)
        const textX = imageX + imageWidth + 10; // Position the text after the image
        const textY = rectY + rectHeight / 2;
        svg.append("text")
          .attr("x", textX) // Position the text after the image
          .attr("y", textY) // Centered vertically within the rect
          .attr("dy", "0.35em") // Adjust vertical alignment
          .attr("class", "match-timeline-text")
          .style("fill", theme.matchLabel)
          .text("Data from all recorded matches currently being used");
      }
      svg
        .append("line")
        .attr("x1", 0)
        .attr("y1", height / 2 + 40)
        .attr("x2", timelineWidth)
        .attr("y2", height / 2 + 40)
        .style("stroke", theme.playerCardBorderColor)
        .style("stroke-width", 2);
      if (matchData.length > 0) {
        const images = svg
          .selectAll("image")
          .data(matchData)
          .attr("id", (d, i) => `match-icon-${d.matchId}`);
        const previousMatch = matchData.find((match) => match.matchId === selectedMatchId);
        const previousMatchIndex = matchData.findIndex((match) => match.matchId === selectedMatchId!);
        const matchesBefore = matchData.slice(0, previousMatchIndex);
        const matchesAfter = matchData.slice(previousMatchIndex + 1, matchData.length);
        if (previousMatch && zoomLevel === 2) {
          svg
            .append("image")
            .attr("class", "match-icon")
            .style("cursor", "pointer")
            .attr("id", `match-icon-${previousMatchIndex}`)
            .attr("y", height / 2 - 11)
            .style("width", 24)
            .style("height", 24)
            .attr("xlink:href", previousMatch.teamLogoURL ? previousMatch.teamLogoURL : noImageClub)
            .attr("x", xScale(previousMatchIndex) + margin)
            .on("click", (event) => {
              handleMatchClick(previousMatch, event);
            });
          svg
            .append('text')
            .attr('class', 'win-loss-info-text')
            .attr('id', `match-label-${previousMatchIndex}`)
            .attr('y', height / 2 - 30)
            .attr('text-anchor', 'middle')
            .style('fill', theme.matchLabel)
            .text(d => previousMatch.matchLabel)
            .attr('x', xScale(previousMatchIndex) + margin + 10)
            .transition()
            .duration(matchData.length > 0 ? 500 : 0)
            .ease(d3.easeCubicOut)
            .attr('x', xScale(previousMatchIndex) + margin + 10)
          if (matchData.length !== previousMatchIndex + 1) {
            svg
            .append("line")
            .attr('class', 'match-line')
            .attr("id", `match-icon-${previousMatchIndex}`)
            .attr("y1", height / 2 + 10)
            .attr("y2", height / 2 - 10)
            .style('stroke', theme.playerCardBorderColor)
            .style('stroke-width', 2)
            .attr("x2", xScale(previousMatchIndex + 1) - 25)
            .attr("x1", xScale(previousMatchIndex + 1) - 25)
          }
          matchData.length > 0 && selectedMatchId !== 0 &&
            svg
              .append("line")
              .attr("y1", height / 2 + 40)
              .attr("y2", height / 2 + 40)
              .style("stroke-width", 3)
              .style("stroke", "#18b686")
              .attr("x2", timelineWidth)
              .attr("x1", 0)
              .transition()
              .duration(500)
              .ease(d3.easeCubicOut)
              .attr("x1", xScale(previousMatchIndex) + 30)
              .attr("x2", xScale(previousMatchIndex) + 70)
              .transition()
              .duration(500)
              .ease(d3.easeCubicOut)
              .attr("x2", timelineWidth)
              .attr("x1", 0);
          if (matchesBefore.length > 0) {
            setTimeout(() => {
              matchesBefore.map((match, i) => {
                svg
                  .append("image")
                  .attr("class", "match-icon")
                  .style("cursor", "pointer")
                  .attr("id", `match-icon-${i}`)
                  .attr("y", height / 2 - 11)
                  .style("width", 24)
                  .style("height", 24)
                  .attr("xlink:href", match.teamLogoURL ? match.teamLogoURL : noImageClub)
                  .on("click", (event) => {
                    handleMatchClick(match, event);
                  })
                  .attr("x", ((matchesBefore.length - i) * -60) + margin)
                  .transition()
                  .duration(matchesBefore.length > 0 ? 500 : 0)
                  .ease(d3.easeCubicOut)
                  .attr("x", xScale(i) + margin);
                svg
                  .append('line')
                  .attr('class', 'match-line')
                  .attr('id', `match-line-${i}`)
                  .attr('y1', height / 2 + 10)
                  .attr('y2', height / 2 - 10)
                  .style('stroke', theme.playerCardBorderColor)
                  .style('stroke-width', 2)
                  .attr('x1', ((matchesBefore.length - i) * -60) - 25)
                  .attr('x2', ((matchesBefore.length - i) * -60) - 25)
                  .transition()
                  .duration(matchData.length > 0 ? 500 : 0)
                  .ease(d3.easeCubicOut)
                  .attr('x1', xScale(i + 1) - 25)
                  .attr('x2', xScale(i + 1) - 25)
                svg
                  .append('text')
                  .attr('class', 'win-loss-info-text')
                  .attr('id', `match-label-${i}`)
                  .attr('y', height / 2 - 30)
                  .attr('text-anchor', 'middle')
                  .style('fill', theme.matchLabel)
                  .text(d => match.matchLabel)
                  .attr('x', ((matchesBefore.length - i) * -60) + margin + 10)
                  .transition()
                  .duration(matchData.length > 0 ? 500 : 0)
                  .ease(d3.easeCubicOut)
                  .attr('x', xScale(i) + margin + 10)
              });
            }, 500);
          }
          if (matchesAfter.length > 0) {
            setTimeout(() => {
              matchesAfter.map((match, i) => {
                svg
                  .append("image")
                  .attr("class", "match-icon")
                  .style("cursor", "pointer")
                  .attr("id", `match-icon-${i + previousMatchIndex}`)
                  .attr("y", height / 2 - 11)
                  .style("width", 24)
                  .style("height", 24)
                  .attr("xlink:href", match.teamLogoURL ? match.teamLogoURL : noImageClub)
                  .on("click", (event) => {
                    handleMatchClick(match, event);
                  })
                  .attr("x", timelineWidth + ((matchesAfter.length - i) * 60) + margin)
                  .transition()
                  .duration(matchesAfter.length > 0 ? 500 : 0)
                  .ease(d3.easeCubicOut)
                  .attr("x", xScale(i + 1 + previousMatchIndex) + margin);
                svg
                  .append('line')
                  .attr('class', 'match-line')
                  .attr('id', `match-line-${i + previousMatchIndex}`)
                  .attr('y1', height / 2 + 10)
                  .attr('y2', height / 2 - 10)
                  .style('stroke', theme.playerCardBorderColor)
                  .style('stroke-width', i === (matchesAfter.length - 1) ? 0 : 2)
                  .attr('x1', timelineWidth + ((matchesAfter.length - i) * 60) - 25)
                  .attr('x2', timelineWidth + ((matchesAfter.length - i) * 60) - 25)
                  .transition()
                  .duration(matchData.length > 0 ? 500 : 0)
                  .ease(d3.easeCubicOut)
                  .attr('x1', xScale(i + previousMatchIndex + 2) - 25)
                  .attr('x2', xScale(i + previousMatchIndex + 2) - 25)
                svg
                  .append('text')
                  .attr('class', 'win-loss-info-text')
                  .attr('id', `match-label-${i + previousMatchIndex}`)
                  .attr('y', height / 2 - 30)
                  .attr('text-anchor', 'middle')
                  .style('fill', theme.matchLabel)
                  .text(d => match.matchLabel)
                  .attr('x', timelineWidth + ((matchesAfter.length - i) * 60) + margin + 10)
                  .transition()
                  .duration(matchData.length > 0 ? 500 : 0)
                  .ease(d3.easeCubicOut)
                  .attr('x', xScale(i + 1 + previousMatchIndex) + margin + 10)
              });
            }, 500);
          }
        } else {
          images.enter()
            .append('image')
            .attr('class', 'match-icon')
            .style("cursor", "pointer")
            .attr('id', (d, i) => `match-icon-${i}`)
            .attr('y', height / 2 - 11)
            .style('width', 24)
            .style('height', 24)
            .attr('xlink:href', d => d.teamLogoURL ? d.teamLogoURL : noImageClub)
            .attr('x', (d, i) => xScale(i) + margin)
            .transition()
            .duration(matchData.length > 0 ? 500 : 0) // Only animate if matchData.length > 0
            .ease(d3.easeCubicOut)
            .attr('x', (_, i) => direction === 'next' ? ((matchData.length - i) * -60) - 20 : timelineWidth + 50 + (i * 60))
            .transition()
            .duration(matchData.length > 0 ? 500 : 0) // Only animate if matchData.length > 0
            .ease(d3.easeCubicOut)
            .attr('x', (d, i) => xScale(i) + margin + (i===0 ? 12 : 0))
            .on('end', function (d, i) {
              d3.select(this).on('click', (event, d) => {
                handleMatchClick(matches[i], event);
              });
            });
          images.exit().remove();
          const lines = svg
            .selectAll(".match-line")
            .data(matchData.slice(0, -1))
            .attr("id", (d, i) => `match-line-${i}`);
          lines.enter()
            .append('line')
            .attr('class', 'match-line')
            .attr('id', (d, i) => `match-line-${i}`)
            .attr('y1', height / 2 + 10)
            .attr('y2', height / 2 - 10)
            .style('stroke', theme.playerCardBorderColor)
            .style('stroke-width', 2)
            .attr('x1', (d, i) => xScale(i + 1) - 25)
            .attr('x2', (d, i) => xScale(i + 1) - 25)
            .transition()
            .duration(matchData.length > 0 ? 500 : 0) // Only animate if matchData.length > 0
            .ease(d3.easeCubicOut)
            .attr('x1', (_, i) => direction === 'next' ? ((matchData.length - i) * -60) - 20 : timelineWidth + 50 + (i * 60))
            .attr('x2', (_, i) => direction === 'next' ? ((matchData.length - i) * -60) - 20 : timelineWidth + 50 + (i * 60))
            .transition()
            .duration(matchData.length > 0 ? 500 : 0) // Only animate if matchData.length > 0
            .ease(d3.easeCubicOut)
            .attr('x1', (d, i) => xScale(i + 1) - 25)
            .attr('x2', (d, i) => xScale(i + 1) - 25)
          lines.exit().remove();
          const labels = svg.selectAll(".match-label").data(matchData); // Use matchId as key
          labels.enter()
            .append('text')
            .attr('class', 'win-loss-info-text')
            .attr('id', (d, i) => `match-label-${i}`)
            .attr('y', height / 2 - 30)
            .attr('text-anchor', 'middle')
            .style('fill', theme.matchLabel)
            .text(d => d.matchLabel)
            .attr('x', (d, i) => xScale(i) + margin + 10)
            .transition()
            .duration(matchData.length > 0 ? 500 : 0) // Only animate if matchData.length > 0
            .ease(d3.easeCubicOut)
            .attr('x', (_, i) => direction === 'next' ? ((matchData.length - i) * -60) - 20 : timelineWidth + 50 + (i * 60))
            .transition()
            .duration(matchData.length > 0 ? 500 : 0) // Only animate if matchData.length > 0
            .ease(d3.easeCubicOut)
            .attr('x', (d, i) => xScale(i) + margin + 10 + (i===0 ? 12 : 0));
          labels.exit().remove();
          matchData.length > 0 &&
            svg
              .append("line")
              .attr("y1", height / 2 + 40)
              .attr("y2", height / 2 + 40)
              .style("stroke-width", 3)
              .style("stroke", "#18b686")
              .attr(direction === "next" ? "x1" : "x2", direction === "next" ? 0 : timelineWidth)
              .attr(direction === "next" ? "x2" : "x1", direction === "next" ? timelineWidth : 0)
              .transition()
              .duration(500)
              .ease(d3.easeCubicOut)
              .attr(direction === "next" ? "x2" : "x1", direction === "next" ? -50 : timelineWidth + 50)
              .transition()
              .duration(500)
              .ease(d3.easeCubicOut)
              .attr(direction === "next" ? "x2" : "x1", direction === "next" ? timelineWidth : 0);
        }
      }
      // Render solid line at the start
      svg
        .append("line")
        .attr("x1", 0)
        .attr("y1", 40)
        .attr("x2", 0)
        .attr("y2", 60)
        .style("stroke-width", 4)
        .style("stroke", theme.playerCardBorderColor)
        .style("opacity", 0)
        .transition()
        .duration(matchData.length > 0 ? 500 : 0)
        .ease(d3.easeCubicOut)
        .style("opacity", 1);
      // Render solid line at the end
      svg
        .append("line")
        .attr("x1", timelineWidth)
        .attr("y1", 40)
        .attr("x2", timelineWidth)
        .attr("y2", 60)
        .style("stroke-width", 4)
        .style("stroke", theme.playerCardBorderColor)
        .style("opacity", 0)
        .transition()
        .duration(matchData.length > 0 ? 500 : 0)
        .ease(d3.easeCubicOut)
        .style("opacity", 1);
    }, [matchData, theme, zoomLevel, teamId]);

    useEffect(() => {
      if (direction !== "") {
        createMatchTimeline();
        window.addEventListener("resize", createMatchTimeline);
        window.addEventListener("orientationchange", createMatchTimeline);
        return () => {
          window.removeEventListener("resize", createMatchTimeline);
          window.removeEventListener("orientationchange", createMatchTimeline);
        };
      }
    }, [matchData, theme, zoomLevel, teamId]);

    return (
      <div id='match-timeline-container' className='match-timeline-container'>
        <svg ref={svgRef} width="100%" height={100} />
        {isMatchCardOpen && matchCardPosition &&
          (
            <div
              style={{
                position: "absolute",
                left: `${matchCardPosition.x}px`,
                top: `${matchCardPosition.y + 20}px`,
                zIndex: 1000,
              }}
            >
              <MatchDetailCard matchData={matchCardDetails} matchId={selectedMatch} goToMatch={(matchId) => onSelectMatchId(matchId)}
                closeMatchCard={() => setIsMatchCardOpen(false)} />
            </div>
          )}
      </div>
    );
  }
);

export default MatchTimeLine;
